import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

/**
 * Utility functions for maintaining data integrity in organization members
 */

export interface OrphanedMember {
  id: string;
  organization_id: string;
  email: string;
  full_name: string;
  created_at: string;
}

export interface DuplicateEmail {
  email: string;
  member_records: Array<{
    id: string;
    organization_id: string;
    auth_user_id: string | null;
  }>;
}

/**
 * Find orphaned member records (members without auth_user_id)
 */
export const findOrphanedMembers = async (): Promise<OrphanedMember[]> => {
  try {
    const { data, error } = await supabase.functions
      .invoke('cleanup-member-data', {
        body: { action: 'find_orphaned' }
      });

    if (error) throw error;
    return data.orphaned_members || [];
  } catch (error) {
    console.error('Error finding orphaned members:', error);
    throw error;
  }
};

/**
 * Clean up orphaned member records
 */
export const cleanupOrphanedMembers = async (memberIds: string[]): Promise<number> => {
  try {
    const { data, error } = await supabase.functions
      .invoke('cleanup-member-data', {
        body: { 
          action: 'cleanup_orphaned',
          member_ids: memberIds
        }
      });

    if (error) throw error;
    
    toast.success(`Cleaned up ${memberIds.length} orphaned member records`);
    return memberIds.length;
  } catch (error) {
    console.error('Error cleaning up orphaned members:', error);
    toast.error('Failed to cleanup orphaned members');
    throw error;
  }
};

/**
 * Find duplicate email addresses across organizations
 */
export const findDuplicateEmails = async (): Promise<DuplicateEmail[]> => {
  try {
    const { data, error } = await supabase
      .from('organization_members')
      .select('id, organization_id, email, auth_user_id');

    if (error) throw error;

    // Group by email and find duplicates
    const emailGroups = (data || []).reduce((acc, member) => {
      if (!acc[member.email]) {
        acc[member.email] = [];
      }
      acc[member.email].push({
        id: member.id,
        organization_id: member.organization_id,
        auth_user_id: member.auth_user_id
      });
      return acc;
    }, {} as Record<string, Array<{ id: string; organization_id: string; auth_user_id: string | null }>>);

    // Return only emails with multiple records
    return Object.entries(emailGroups)
      .filter(([, records]) => records.length > 1)
      .map(([email, member_records]) => ({ email, member_records }));
  } catch (error) {
    console.error('Error finding duplicate emails:', error);
    throw error;
  }
};

/**
 * Check if an email can be safely added to an organization
 */
export const canAddEmailToOrganization = async (
  email: string, 
  organizationId: string
): Promise<{ canAdd: boolean; reason?: string; existingMember?: any }> => {
  try {
    const { data, error } = await supabase
      .from('organization_members')
      .select('id, organization_id, auth_user_id, full_name')
      .eq('email', email.toLowerCase());

    if (error) throw error;

    if (!data || data.length === 0) {
      return { canAdd: true };
    }

    // Check if already in this organization
    const existingInOrg = data.find(member => member.organization_id === organizationId);
    if (existingInOrg) {
      return { 
        canAdd: false, 
        reason: 'Email already exists in this organization',
        existingMember: existingInOrg
      };
    }

    // Check for orphaned records
    const orphanedRecord = data.find(member => !member.auth_user_id);
    if (orphanedRecord) {
      return {
        canAdd: false,
        reason: 'Email has orphaned record that needs cleanup',
        existingMember: orphanedRecord
      };
    }

    // If user exists in other organizations with auth_user_id, they can be added
    return { canAdd: true };
  } catch (error) {
    console.error('Error checking email availability:', error);
    throw error;
  }
};

/**
 * Run a comprehensive data integrity check
 */
export const runDataIntegrityCheck = async () => {
  try {
    console.log('Running data integrity check...');
    
    const [orphanedMembers, duplicateEmails] = await Promise.all([
      findOrphanedMembers(),
      findDuplicateEmails()
    ]);

    const report = {
      orphanedMembers: orphanedMembers.length,
      duplicateEmails: duplicateEmails.length,
      issues: [
        ...orphanedMembers.map(member => ({
          type: 'orphaned_member',
          email: member.email,
          id: member.id,
          organization_id: member.organization_id
        })),
        ...duplicateEmails.map(duplicate => ({
          type: 'duplicate_email',
          email: duplicate.email,
          count: duplicate.member_records.length
        }))
      ]
    };

    console.log('Data integrity report:', report);
    return report;
  } catch (error) {
    console.error('Error running data integrity check:', error);
    throw error;
  }
};